"use client";

import { c as _c } from "react/compiler-runtime";
import { useDesktopAnimations } from "@/lib/breakpoints";
import React, { useRef } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { smartB2BScreens } from "./brokerage.data";
import "swiper/css";
const ease = [0.17, 0.85, 0.438, 0.99] as const;
const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='620'%3E%3Crect width='300' height='620' rx='24' fill='%23f1f4f9'/%3E%3C/svg%3E";
const ArrowButton = t0 => {
  const $ = _c(6);
  const {
    direction,
    onClick
  } = t0;
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
  const t1 = direction === "prev" ? "Previous slide" : "Next slide";
  let t2;
  if ($[0] !== Icon) {
    t2 = <Icon className="h-5 w-5" strokeWidth={2.5} />;
    $[0] = Icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onClick || $[3] !== t1 || $[4] !== t2) {
    t3 = <button type="button" onClick={onClick} aria-label={t1} className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-[#f5497e] text-white shadow-[0_8px_24px_-8px_rgba(245,73,126,0.8)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#e83a70]">{t2}</button>;
    $[2] = onClick;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
export default function SmartB2B() {
  const $ = _c(26);
  const desktopAnimations = useDesktopAnimations();
  const swiperRef = useRef(null);
  let t0;
  if ($[0] !== desktopAnimations) {
    t0 = desktopAnimations ? {
      opacity: 0,
      y: 40
    } : false;
    $[0] = desktopAnimations;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== desktopAnimations) {
    t1 = desktopAnimations ? {
      opacity: 1,
      y: 0
    } : undefined;
    $[2] = desktopAnimations;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== desktopAnimations) {
    t2 = desktopAnimations ? undefined : {
      opacity: 1,
      y: 0
    };
    $[4] = desktopAnimations;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      duration: 0.6,
      ease
    };
    t4 = {
      once: true
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t5 = <motion.h2 initial={t0} whileInView={t1} animate={t2} transition={t3} viewport={t4} className="font-poppins mb-5 text-[30px] font-bold leading-[1.35] text-[#0b1354] sm:text-[36px] lg:text-[40px] dark:text-white">{"Smart & Simple B2B Order Management"}</motion.h2>;
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className="mx-auto max-w-[820px] text-[16px] leading-[31px] text-[#6b7280] md:text-[17px] dark:text-gray-400">Manage orders, invoices, and payments seamlessly with our intuitive Order Management Software.</p>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5) {
    t7 = <div className="mx-auto mb-14 max-w-[900px] px-6 text-center">{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [Autoplay];
    t9 = swiper => {
      swiperRef.current = swiper;
    };
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      delay: 3000,
      disableOnInteraction: false
    };
    t11 = {
      480: {
        slidesPerView: 1.6,
        spaceBetween: 24
      },
      640: {
        slidesPerView: 2.2,
        spaceBetween: 28
      },
      1024: {
        slidesPerView: 3.4,
        spaceBetween: 32
      },
      1280: {
        slidesPerView: 5,
        spaceBetween: 24,
        centeredSlides: false
      }
    };
    $[17] = t10;
    $[18] = t11;
  } else {
    t10 = $[17];
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="sm:px-[105px]"><Swiper modules={t8} onSwiper={t9} spaceBetween={20} slidesPerView={1.15} centeredSlides={true} loop={true} autoplay={t10} breakpoints={t11} className="!px-2 !pb-4">{smartB2BScreens.map(_temp2)}</Swiper></div>;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="pointer-events-auto ml-2"><ArrowButton direction="prev" onClick={() => swiperRef.current?.slidePrev()} /></div>;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="pointer-events-none absolute inset-y-0 left-0 right-0 z-10 hidden items-center justify-between sm:flex">{t13}<div className="pointer-events-auto mr-2"><ArrowButton direction="next" onClick={() => swiperRef.current?.slideNext()} /></div></div>;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ArrowButton direction="prev" onClick={() => swiperRef.current?.slidePrev()} />;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="relative w-full px-4">{t12}{t14}<div className="mt-6 flex items-center justify-center gap-4 sm:hidden">{t15}<ArrowButton direction="next" onClick={() => swiperRef.current?.slideNext()} /></div></div>;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t7) {
    t17 = <section className="overflow-hidden bg-white py-16 font-sans md:py-24 dark:bg-gray-950"><div className="w-full">{t7}{t16}</div></section>;
    $[24] = t7;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  return t17;
}
function _temp2(screen) {
  return <SwiperSlide key={screen.src}><div className="mx-auto w-full max-w-[340px]"><Image src={screen.src} alt={screen.alt} width={300} height={620} loading="lazy" className="h-auto w-full rounded-[24px] object-contain" onError={_temp} /></div></SwiperSlide>;
}
function _temp(e) {
  e.currentTarget.onerror = null;
  e.currentTarget.src = PLACEHOLDER;
}